// Saved views for CDI Operations Reporting V2 (Vytalize review).
// V2-only — V1 continues to use Coding_Operations_Views.js.
// Built-in presets match Vytalize's Reporting Needs doc.
// Group chips serialize as stable string keys ('practice', 'vendor', 'coder')
// and resolve to entity-type IDs at runtime so views are portable across tenants.

const STORAGE_KEY = 'coding_ops_v2_user_views_v1';
const LAST_VIEW_KEY = 'coding_ops_v2_last_view_v1';

const ENTITY_TYPE_KEY_MAP = {
  practice: 'Practice',
  vendor: 'Vendor',
  coder: 'Coding Analyst',
};

// Display labels for backend PROGRAM values. Backend data stays as
// 'Suspect' / 'Early Start' / 'Resub'; these only affect rendering in V2
// tabs, cells, and group headers.
export const PROGRAM_LABELS = {
  'Suspect': 'PVP',
  'Early Start': 'Practice Foundations',
  'Resub': 'Resubmissions',
};

export function formatProgram(v) {
  return PROGRAM_LABELS[v] || v;
}

// Shared opportunity-grain drill columns (Report 2.b and 3.b).
// Matches Vytalize's Reporting Needs spec, with Last Appt Date added back as
// extra context. Level Assignment Date = current tier's task creation date;
// Reviewed-by-next-appt compares opp resolution to the first appt scheduled
// on/after that assignment date.
const OPP_DRILL_DETAIL_COLUMNS = [
  { header: 'Patient', field: 'patient_name', align: 'left', blur: 'sensitive' },
  { header: 'Program', field: 'program', align: 'center',
    format: formatProgram,
    color: v => ({ Suspect: '#4C6E97', Resub: '#9F4D30', 'Early Start': '#7C3AED' }[v]) },
  { header: 'Level of Review', field: 'tier_progression', align: 'left' },
  { header: 'No Findings',
    compute: r => r.total_codes == null ? null : (r.total_codes === 0 ? 'Yes' : 'No') },
  { header: 'Suspects', field: 'suspect_code_count' },
  { header: 'Recaptures', field: 'recapture_code_count' },
];

export const BUILTIN_PRESETS = [
  {
    key: 'coder_productivity_qa',
    name: 'Coder Productivity & QA',
    // Locked customer report. Columns rename overloaded fields to a rejected-QA
    // context (e.g. qa_explanation → 'Reject/Error Explanation'), which is only
    // truthful under the rejected-only drill filter. So grain, grouping, and the
    // semantic filters are frozen — only date and entity scope may change.
    locked: true,
    state: {
      activeGrain: 'code',
      groupChips: ['coder'],
      activeMetricFilter: [],
      activeStatusFilter: [],
      activeTierFilter: [],
      activeCoderFilter: [],
      activeQaResultFilter: [],
      activeStaleOnly: false,
      activeIncludeExpired: false,
      activeAdminClosed: false,
      activeDatePreset: 'mtd',
      dateStart: null,
      dateEnd: null,
    },
    // Applied on drill-in only; original filter state restored on navigate-back.
    detailFilters: {
      activeQaResultFilter: ['rejected'],
    },
    // Group_1 (Coding Analyst) is auto-prepended at render time.
    // Avg Charts / Day & / Hour are task-grain throughput (completed charts ÷
    // active days, ÷ 8 for /hour), joined per coder onto the code-grain QA roll-up.
    // Blank for a coder with no completed charts in the window.
    columns: {
      aggregate: [
        { header: 'QA Reviewed', field: 'qa_reviewed_count' },
        { header: 'QA Accepted', field: 'qa_accepted', color: v => v > 0 ? '#389A6C' : null },
        { header: 'QA Rejected', field: 'qa_rejected', color: v => v > 0 ? '#DC2626' : null },
        { header: 'QA Pending', field: 'qa_pending', color: v => v > 0 ? '#D97706' : null },
        { header: 'QA Rate %', field: 'qa_rate', format: 'pct',
          color: v => { if (v == null) return null; return v >= 0.9 ? '#389A6C' : v < 0.7 ? '#DC2626' : '#D97706'; },
          bold: v => v != null },
        { header: 'Avg Charts / Day', field: 'charts_per_day' },
        { header: 'Avg Charts / Hour', field: 'charts_per_hour' },
      ],
      detail: [
        { header: 'Coding Analyst', field: 'coder_entity_name', align: 'left', blur: 'sensitive' },
        { header: 'Rejected ICD-10 Codes', field: 'icd_code', bold: () => true },
        { header: 'HCC', field: 'hcc_code' },
        { header: 'Reject/Error Reason', field: 'qa_disagree_reason', align: 'left' },
        { header: 'Reject/Error Explanation', field: 'qa_explanation', align: 'left' },
        { header: 'Program', field: 'program', align: 'center',
          format: formatProgram,
          color: v => ({ Suspect: '#4C6E97', Resub: '#9F4D30', 'Early Start': '#7C3AED' }[v]) },
        { header: 'Review Level', field: 'tier', align: 'center' },
        { header: 'Practice/TIN', align: 'left', blur: 'sensitive',
          compute: r => {
            const name = r.practice_name || '';
            const tin = r.practice_tin || '';
            if (name && tin) return `${name} (${tin})`;
            return name || tin || '';
          } },
      ],
    },
  },
  {
    key: 'cdi_program_status',
    name: 'CDI Program Status',
    // Group roll-up is terminal — no per-chart drill, no side panel.
    disableDrilldown: true,
    // Locked customer report — aggregates are defined over the full population;
    // changing a filter would silently recompute them. Date/entity scope only.
    locked: true,
    state: {
      activeGrain: 'opportunity',
      groupChips: ['program', 'tier'],
      activeMetricFilter: [],
      activeStatusFilter: [],
      activeTierFilter: [],
      activeCoderFilter: [],
      activeQaResultFilter: [],
      activeStaleOnly: false,
      activeIncludeExpired: false,
      activeAdminClosed: false,
      activeDatePreset: 'ytd',
      dateStart: null,
      dateEnd: null,
    },
    // Group_1 (Program) and group_2 (Level) are auto-prepended.
    // Blank columns are placeholders pending backend aggregates or Vytalize definition.
    columns: {
      aggregate: [
        { header: 'Open Charts', field: 'active_count' },
        { header: 'Closed Charts', field: 'completed_count', color: v => v > 0 ? '#389A6C' : null },
        { header: 'Expired Charts', field: 'expired_count',
          color: v => v > 0 ? '#9CA3AF' : null,
          showIfState: vm => vm.activeIncludeExpired === true },
        { header: 'Total Patient Charts', field: 'opportunity_count' },
        { header: 'Completion Rate', format: 'pct',
          compute: r => (r.opportunity_count > 0)
            ? r.completed_count / r.opportunity_count : null,
          color: v => { if (v == null) return null; return v >= 0.8 ? '#389A6C' : v < 0.4 ? '#DC2626' : '#D97706'; },
          bold: v => v != null },
        { header: 'Suspect Code Yield', field: 'suspect_code_yield' },
        { header: 'Recapture Code Yield', field: 'recapture_code_yield' },
        { header: 'Recapture HCC Yield per pt', field: 'recapture_hcc_yield_per_pt' },
        { header: 'Suspect HCC Yield per pt', field: 'suspect_hcc_yield_per_pt' },
        { header: '% No Findings', field: 'no_findings_pct', format: 'pct',
          color: v => { if (v == null) return null; return v > 0.5 ? '#DC2626' : v > 0.3 ? '#D97706' : '#389A6C'; } },
        { header: 'Suspects Identified', field: 'suspect_total' },
        { header: 'Recaptures Identified', field: 'recapture_total' },
        { header: 'TAT', field: 'avg_turnaround' },
        { header: 'Suspect Code Acceptance Rate', field: 'suspect_qa_rate', format: 'pct',
          color: v => { if (v == null) return null; return v >= 0.9 ? '#389A6C' : v < 0.7 ? '#DC2626' : '#D97706'; },
          bold: v => v != null },
      ],
    },
  },
  {
    key: 'practice_status_by_program',
    name: 'Practice Status by CDI Program',
    // Locked customer report — same rationale as CDI Program Status: aggregates
    // are defined over the full population. Date/entity scope only.
    locked: true,
    state: {
      activeGrain: 'opportunity',
      groupChips: ['practice', 'program'],
      activeMetricFilter: [],
      activeStatusFilter: [],
      activeTierFilter: [],
      activeCoderFilter: [],
      activeQaResultFilter: [],
      activeStaleOnly: false,
      activeIncludeExpired: false,
      activeAdminClosed: false,
      activeDatePreset: 'ytd',
      dateStart: null,
      dateEnd: null,
    },
    // Group_1 (Practice) and group_2 (Program) are auto-prepended.
    // Blank columns are placeholders pending backend aggregates / fields.
    columns: {
      aggregate: [
        { header: 'Open Charts', field: 'active_count' },
        { header: 'Closed Charts', field: 'completed_count', color: v => v > 0 ? '#389A6C' : null },
        { header: 'Expired Charts', field: 'expired_count',
          color: v => v > 0 ? '#9CA3AF' : null,
          showIfState: vm => vm.activeIncludeExpired === true },
        { header: 'Total Patient Charts', field: 'opportunity_count' },
        { header: 'Completion Rate', format: 'pct',
          compute: r => (r.opportunity_count > 0) ? r.completed_count / r.opportunity_count : null,
          color: v => { if (v == null) return null; return v >= 0.8 ? '#389A6C' : v < 0.4 ? '#DC2626' : '#D97706'; },
          bold: v => v != null },
        { header: 'Codes / Patient', field: 'code_yield' },
        { header: 'HCCs / Patient', field: 'hcc_yield_per_pt' },
        { header: '% in 1LR', format: 'pct',
          compute: r => (r.opportunity_count > 0) ? r.tier_1lr_count / r.opportunity_count : null },
        { header: '% in 2LR', format: 'pct',
          compute: r => (r.opportunity_count > 0) ? r.tier_2lr_count / r.opportunity_count : null },
        { header: '% in 3LR', format: 'pct',
          compute: r => (r.opportunity_count > 0) ? r.tier_3lr_count / r.opportunity_count : null },
        { header: '% in Confirm', format: 'pct',
          compute: r => (r.opportunity_count > 0) ? r.tier_confirm_count / r.opportunity_count : null },
        { header: '% No Findings', field: 'no_findings_pct', format: 'pct',
          color: v => { if (v == null) return null; return v > 0.5 ? '#DC2626' : v > 0.3 ? '#D97706' : '#389A6C'; } },
        { header: 'Suspects Identified', field: 'suspect_total' },
        { header: 'Recaptures Identified', field: 'recapture_total' },
      ],
      detail: OPP_DRILL_DETAIL_COLUMNS,
    },
  },
  {
    key: 'explore_all',
    name: 'Explore — All Data',
    // Unlocked general-purpose explorer. No `columns` override, so the grid
    // renders the neutral grain-default columns (un-renamed: 'Explanation',
    // 'QA Result', 'Reject Reason', …) rather than a report's context-specific
    // labels. Grain toggle, grouping, filters, and dates are all free — flip the
    // grain to pivot between code / opportunity / task rows over the same dataset.
    // Defaults to code grain (richest detail) grouped by practice. The code
    // default now exposes the full row (MBI/DOB/Dx/PCP/TIN/submitted-date), so
    // this supersedes the old 'Suspect + Recapture - Patient Detail' preset.
    state: {
      activeGrain: 'code',
      groupChips: ['practice'],
      activeMetricFilter: [],
      activeStatusFilter: [],
      activeTierFilter: [],
      activeCoderFilter: [],
      activeQaResultFilter: [],
      activeStaleOnly: false,
      activeIncludeExpired: false,
      activeAdminClosed: false,
      activeDatePreset: 'ytd',
      dateStart: null,
      dateEnd: null,
    },
  },
];

export function resolveGroupChip(key, entityTypes) {
  if (typeof key === 'number') return key;
  if (key === 'program' || key === 'tier' || key === 'status') return key;
  const typeName = ENTITY_TYPE_KEY_MAP[key];
  if (!typeName) return null;
  const et = entityTypes.find(e => e.type_name === typeName);
  return et ? et.id : null;
}

export function serializeGroupChip(value, entityTypes) {
  if (typeof value === 'string') return value;
  const et = entityTypes.find(e => e.id === value);
  if (!et) return null;
  for (const [key, name] of Object.entries(ENTITY_TYPE_KEY_MAP)) {
    if (et.type_name === name) return key;
  }
  return null;
}

export function loadUserViews() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveUserViews(views) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(views));
}

export function loadLastViewKey() {
  return localStorage.getItem(LAST_VIEW_KEY);
}

export function saveLastViewKey(key) {
  if (key) localStorage.setItem(LAST_VIEW_KEY, key);
  else localStorage.removeItem(LAST_VIEW_KEY);
}
